import { useContext } from 'react';
import Compare from '../Compare';
import Stack from '../../../shared/layouts/Stack';
import CategoryHeader from '../CategoryHeader';
import { CompareContext } from '../ComparePage';
import { cloneDeep } from 'lodash';
import { number } from "prop-types";
import {useTranslation} from "react-i18next";

const SECTION = 'orv_coverage';

const CompareOrvCoverageSection = ({ insuranceIndex }) => {
  const {
    showOnlyDiff,
    hasDiff,
    shouldShow,
    ownInsuranceData,
    handleInsuranceDataChange,
    otherInsuranceData,
    orvCoverageFields,
    hasSectionCopiedFields,
  } = useContext(CompareContext);

  const { t } = useTranslation();

  const handleOrvCoverageDataChange =
    (index, section, fieldName) => ({ target: { name, value } }) => {
      const coverages = cloneDeep(
        ownInsuranceData[insuranceIndex]?.coverages || []
      );

      coverages[index] = {
        ...(coverages[index] || {}),
        [name]: value,
      };

      handleInsuranceDataChange(
        {
          target: {
            name: 'coverages',
            value: coverages,
          },
        },
        insuranceIndex,
        section,
        fieldName
      );
    };

  const ownCoverages = ownInsuranceData?.[insuranceIndex]?.coverages || [];
  const otherCoverages = otherInsuranceData?.[insuranceIndex]?.coverages || [];
  const renderCoverages =
    ownCoverages?.length > otherCoverages?.length
      ? ownCoverages
      : otherCoverages;

  return (
    renderCoverages?.map((coverage, index) => {
      const ownCoverage = ownCoverages?.[index] || {};
      const otherCoverage = otherCoverages?.[index] || {};
      const SECTION_INSURANCE_COVERAGE = `${SECTION}_${insuranceIndex}${index}`;

      if (
        showOnlyDiff &&
        !hasSectionCopiedFields(SECTION_INSURANCE_COVERAGE) &&
        !hasDiff(orvCoverageFields, ownCoverage, otherCoverage)
      )
        return null;

      return (
        <Stack key={`coverage_${insuranceIndex}.${index}`} space="l">
          <CategoryHeader>
            <span>{t('Verzekerde {{index}}', {index: index + 1})}</span>
            <span>{t('Verzekerde {{index}}', {index: index + 1})}</span>
          </CategoryHeader>
          <Stack space="m">
            {shouldShow(
              'policy_holder',
              ownCoverage,
              otherCoverage,
              SECTION_INSURANCE_COVERAGE
            ) && (
              <Compare
                label={t("Verzekeringnemer")}
                name="policy_holder"
                section={SECTION_INSURANCE_COVERAGE}
                leftValue={ownCoverage?.policy_holder}
                rightValue={otherCoverage?.policy_holder}
                onUpdate={handleOrvCoverageDataChange(
                  index,
                  SECTION_INSURANCE_COVERAGE,
                  'policy_holder'
                )}
              />
            )}
            {shouldShow(
              'covered',
              ownCoverage,
              otherCoverage,
              SECTION_INSURANCE_COVERAGE
            ) && (
              <Compare
                label={t("Verzekerde")}
                name="covered"
                section={SECTION_INSURANCE_COVERAGE}
                leftValue={ownCoverage?.covered}
                rightValue={otherCoverage?.covered}
                onUpdate={handleOrvCoverageDataChange(
                  index,
                  SECTION_INSURANCE_COVERAGE,
                  'covered'
                )}
              />
            )}
            {shouldShow(
              'coverage',
              ownCoverage,
              otherCoverage,
              SECTION_INSURANCE_COVERAGE
            ) && (
              <Compare
                label={t("Dekking")}
                name="coverage"
                section={SECTION_INSURANCE_COVERAGE}
                leftValue={ownCoverage?.coverage}
                rightValue={otherCoverage?.coverage}
                onUpdate={handleOrvCoverageDataChange(
                  index,
                  SECTION_INSURANCE_COVERAGE,
                  'coverage'
                )}
                isCurrency
              />
            )}
            {shouldShow(
              'start_date',
              ownCoverage,
              otherCoverage,
              SECTION_INSURANCE_COVERAGE
            ) && (
              <Compare
                label={t("Ingangsdatum")}
                name="start_date"
                section={SECTION_INSURANCE_COVERAGE}
                leftValue={ownCoverage?.start_date}
                rightValue={otherCoverage?.start_date}
                valueTransformer={(value) =>
                  value ? value.split('-') : ['', '', '']
                }
                onUpdate={handleOrvCoverageDataChange(
                  index,
                  SECTION_INSURANCE_COVERAGE,
                  'start_date'
                )}
              />
            )}
            {shouldShow(
              'end_date',
              ownCoverage,
              otherCoverage,
              SECTION_INSURANCE_COVERAGE
            ) && (
              <Compare
                label={t("Einddatum")}
                name="end_date"
                section={SECTION_INSURANCE_COVERAGE}
                leftValue={ownCoverage?.end_date}
                rightValue={otherCoverage?.end_date}
                valueTransformer={(value) =>
                  value ? value.split('-') : ['', '', '']
                }
                onUpdate={handleOrvCoverageDataChange(
                  index,
                  SECTION_INSURANCE_COVERAGE,
                  'end_date'
                )}
              />
            )}
          </Stack>
        </Stack>
      );
    }) || null
  );
};

CompareOrvCoverageSection.propTypes = {
  insuranceIndex: number.isRequired
};

export default CompareOrvCoverageSection;
